import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { cacheable, withTransaction } from '@datorama/akita';
import {
  BcsRenewalClient,
  BcsRenewalClientCommandType,
  BcsRenewalPlanCommandType,
  ChainType,
  MatchType,
  ParentType,
  SearchCriteria,
  BcsRenewalClientState,
  BcsRenewalPlan,
  BcsRenewalPlanState,
  BcsRenewalClientCommand,
} from '@models/configuration/model';
import {
  Division,
  DivisionContact,
  DivisionContactCommandType,
  DivisionContactState,
  Payroll,
} from '@models/profileConfiguration/model';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, mapTo, map } from 'rxjs';
import { DivisionStore, PayrollStore } from 'src/app/state';

import { CoreService, OrderDirection } from '../models/clux/enum';
import { SearchQuery } from '../models/clux/model';
import { Uri } from '../utils/uri';
import { CommandBase, CommandFactory } from '../utils/command-factory';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  private divisionStore = inject(DivisionStore);
  private http = inject(HttpClient);
  private payrollStore = inject(PayrollStore);
  private commandFactory = inject(CommandFactory);

  /**
   * Retrieve the divisions and store in Akita. This function takes no action if the data already exists in Akita.
   * @param clientId: The ID of the client
   * @param divisionId: The ID of the division selected on login for divisionBased Contacts
   */
  public loadDivisions(clientId: string, divisionId?: string): Observable<void> {
    const criteria: SearchCriteria = [
      {
        key: 'parentId',
        value: clientId,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
      {
        key: 'parentType',
        value: ParentType.CLIENT,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
    ];

    if (divisionId) {
      criteria.push({
        key: 'id',
        value: divisionId,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      });
    }
    const query: SearchQuery = {
      orderBy: 'name',
      orderDirection: OrderDirection.ASC,
    };
    const uri = new Uri(`/profile/${clientId}/configuration/division/search`, CoreService.ProfileConfiguration, query);
    const request$ = this.http.post<Division[]>(uri.toString(), criteria).pipe(withTransaction((divisions) => this.divisionStore.set(divisions)));

    return cacheable(this.divisionStore, request$, { emitNext: true }).pipe(mapTo(null));
  }

  /**
   * Retrieve the divisions and store in Akita. This function takes no action if the data already exists in Akita.
   * @param clientId: The ID of the client
   */
  public loadPayroll(clientId: string): Observable<void> {
    const criteria: SearchCriteria = [
      {
        key: 'parentId',
        value: clientId,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
      {
        key: 'parentType',
        value: ParentType.CLIENT,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
    ];
    const uri = new Uri(`/profile/${clientId}/configuration/payroll/search`, CoreService.ProfileConfiguration);
    const request$ = this.http.post<Payroll[]>(uri.toString(), criteria).pipe(withTransaction((payroll) => this.payrollStore.set(payroll)));

    return cacheable(this.payrollStore, request$, { emitNext: true }).pipe(mapTo(null));
  }

  /**
   * Retrieve the active division associations for a contact. Not cached in Akita because the result
   * belongs to the contact currently being edited rather than to the signed in user.
   * @param contactId: The ID of the contact whose division access is being viewed
   */
  public loadDivisionContacts(contactId: string): Observable<DivisionContact[]> {
    const criteria: SearchCriteria = [
      {
        key: 'contactId',
        value: contactId,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
      {
        key: 'currentState',
        value: DivisionContactState.Active,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
    ];
    const uri = new Uri('/profile/*/configuration/divisionContact/search', CoreService.ProfileConfiguration);
    return this.http.post<DivisionContact[]>(uri.toString(), criteria);
  }

  /**
   * Grant or revoke division access by sending one command per changed division association.
   * @param divisionContactUpdates: The division associations to save, each paired with the command to apply
   */
  public saveDivisionContacts(divisionContactUpdates: DivisionContactUpdate[]): Observable<void> {
    if (divisionContactUpdates.length === 0) {
      return of(null);
    }
    const commands = divisionContactUpdates.map(({ divisionContact, command }) => {
      const uri = new Uri(`/profile/*/configuration/divisionContact/${divisionContact.id}/command/${command}`, CoreService.ProfileConfiguration);
      return this.http.put<void>(uri.toString(), this.commandFactory.createCommand<DivisionContact>(divisionContact, command));
    });

    return forkJoin(commands).pipe(mapTo(null));
  }

  public saveBcsRenewalClient(data: BcsRenewalClient, cmdName: BcsRenewalClientCommandType): Observable<SaveBcsRenewalClientResult> {
    const uri = new Uri(`/profile/${data.parentId}/configuration/bcsRenewalClient/${data.id}/command/${cmdName}`, CoreService.Configuration);
    const command = this.commandFactory.createCommand<BcsRenewalClient>(data, cmdName);

    return this.http.put(uri.toString(), command).pipe(
      map((response) => {
        if (cmdName === BcsRenewalClientCommandType.DraftToSubmitted && typeof response === 'string') {
          return {
            status: 200,
            command,
            ticketNumber: response.replace(/"/g, ''),
          };
        }
        return {
          status: 200,
          command,
        };
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          // Special handling for timeouts - don't treat as an error
          // We are assuming FileBound succeeded in creating SR but took longer than 30 seconds
          if (error.status === 0 || error.status === 504) {
            return of({
              status: 504,
              command,
            });
          }

          return of({
            errors: error.error?.message || 'An error occurred',
            status: error.status,
            command,
          });
        }

        return of({
          errors: String(error),
          status: -1,
          command,
        });
      })
    );
  }

  public saveBcsRenewalPlan(data: BcsRenewalPlan, cmdName?: BcsRenewalPlanCommandType): Observable<{ errors?: string; command?: CommandBase<BcsRenewalPlan> }> {
    if (!cmdName) {
      cmdName = data.lastTransition ? BcsRenewalPlanCommandType.DraftToDraft : BcsRenewalPlanCommandType.StartToDraft;
    }
    const uri = new Uri(`/profile/${data.parentId}/configuration/bcsRenewalPlan/${data.id}/command/${cmdName}`, CoreService.Configuration);
    const command = this.commandFactory.createCommand(data, cmdName);

    return this.http.put(uri.toString(), command).pipe(
      map(() => ({ command })),
      catchError((errors: unknown) => of({ errors: String(errors) }))
    );
  }

  public getBcsRenewalPlans(bcsRenewalClientId: string): Observable<BcsRenewalPlan[]> {
    const criteria: SearchCriteria = [
      {
        key: 'parentId',
        value: bcsRenewalClientId,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
      {
        key: 'currentState',
        value: BcsRenewalPlanState.Draft,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
    ];
    const uri = new Uri(`/profile/${bcsRenewalClientId}/configuration/bcsRenewalPlan/search`, CoreService.Configuration);
    return this.http.post<BcsRenewalPlan[]>(uri.toString(), criteria);
  }

  public getBcsRenewalClient(clientId: string): Observable<BcsRenewalClient[]> {
    const criteria: SearchCriteria = [
      {
        key: 'parentId',
        value: clientId,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
      {
        key: 'currentState',
        value: BcsRenewalClientState.Draft,
        matchType: MatchType.EXACT,
        chainType: ChainType.AND,
      },
    ];
    const uri = new Uri(`/profile/${clientId}/configuration/bcsRenewalClient/search`, CoreService.Configuration);
    return this.http.post<BcsRenewalClient[]>(uri.toString(), criteria);
  }
}

export interface DivisionContactUpdate {
  divisionContact: DivisionContact;
  command: DivisionContactCommandType;
}

interface SaveBcsRenewalClientResult {
  command: BcsRenewalClientCommand;
  status: number;
  errors?: string;
  ticketNumber?: string;
}
